import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { CONFIG } from 'src/global-config';

import { HeroSearchChannels } from 'src/components/cmui/hero-search';

import { trackPublicCta } from 'src/sections/common/track-public-cta';
import { usePublicTrialHref } from 'src/sections/common/hooks/model/use-public-trial-href';

import { HOME_HERO_CTA_ID } from '../hooks/model/use-home-sticky-cta';
import { HOME_HERO_TITLE, HOME_HERO_EYEBROW } from '../constants/home-copy';
import { CM, SECTION_PX, PUBLIC_TEXT, PUBLIC_CTA_SX, PUBLIC_MAX_WIDTH } from '../constants/home-theme';

// ----------------------------------------------------------------------

/** 시연 영상 — 데스크톱 16:10(1440×900) · 폰 4:5(1080×1350). 포스터는 각 영상의 첫 프레임이다 */
const HERO_MEDIA_DIR = `${CONFIG.assetsDir}/assets/home/hero`;
type HeroMedia = { webm?: string; mp4: string; poster: string };
const HERO_MEDIA: Record<'desktop' | 'mobile', HeroMedia> = {
  desktop: { webm: `${HERO_MEDIA_DIR}/hero.webm`, mp4: `${HERO_MEDIA_DIR}/hero.mp4`, poster: `${HERO_MEDIA_DIR}/hero-poster.jpg` },
  mobile: { mp4: `${HERO_MEDIA_DIR}/hero-mobile.mp4`, poster: `${HERO_MEDIA_DIR}/hero-poster-mobile.jpg` },
};
/** 첫 화면 단추 둘은 검색칸 아래 보조 줄이라 공용 CTA 보다 한 칸 작다 — 높이 44(넓은 화면 48) · 글자 15 · 폰도 한 줄 */
const HERO_CTA_COMPACT_SX = { height: { xs: 44, md: 48 }, px: { xs: 2, sm: 2.5, md: 3 }, fontSize: 15 } as const;

const HERO_ALT = '검색 · 컨텐츠 허브 · 이미지 검색 · 영상 장면 훑기 · 북마크 · 브랜드 허브 · 크리에이터 허브 · 상품정보 · 추적 화면을 차례로 보여 주는 CatchMoments 시연';

/**
 * 첫 화면 — 누구를 위한 것인지 · 무엇을 하는지 · 검색칸 · 버튼 둘, 그 아래 실제 서비스 화면으로 만든 시연 영상.
 * 🔑 큰 제목은 찾는 일(소재)부터 지켜보는 일(우리 브랜드)까지 양 끝을 잡는다 — 갈래 하나만 말하면 나머지가 안 담긴다.
 * ⛔ 「AI」를 앞세우지 않는다 — 앱 안의 AI 는 지금 닫혀 있다.
 */
export function HomeHeroSection() {
  const trialHref = usePublicTrialHref();
  return (
    <Box component="header" data-section-id="hero" sx={{ px: SECTION_PX, pt: { xs: 6, md: 10 }, pb: { xs: 4, md: 6 } }}>
      <Box sx={{ maxWidth: PUBLIC_MAX_WIDTH, mx: 'auto', textAlign: 'center' }}>
        <Typography sx={{ ...PUBLIC_TEXT.eyebrow, color: CM.primary, mb: 2 }}>{HOME_HERO_EYEBROW}</Typography>

        <Typography component="h1" sx={{ ...PUBLIC_TEXT.display, color: CM.textDark, maxWidth: 820, mx: 'auto', mb: 3 }}>
          {HOME_HERO_TITLE.lead}
          <br />
          <Box component="span" sx={{ color: CM.primary }}>{HOME_HERO_TITLE.accent}</Box>
        </Typography>

        <Typography sx={{ ...PUBLIC_TEXT.lead, color: CM.textMuted, maxWidth: 680, mx: 'auto', mb: 4 }}>
          14개 채널의 뷰티 글, Meta 광고, 판매몰 상품을 한 곳에서 봅니다.
        </Typography>

        {/* 수집 채널 한 줄 — 랜딩 검색칸 아래와 같은 부품 */}
        <Box sx={{ mb: 4 }}>
          <HeroSearchChannels />
        </Box>

        {/* 폰도 한 줄에 같은 폭으로 — 글자를 한 칸 줄여 320px 에서도 한 줄이다 */}
        <Stack id={HOME_HERO_CTA_ID} direction="row" spacing={{ xs: 1, sm: 1.5 }} justifyContent="center" sx={{ '& > *': { whiteSpace: 'nowrap', minWidth: 0 } }}>
          <Button
            component={RouterLink}
            href={trialHref}
            variant="contained"
            onClick={() => trackPublicCta('home.hero.trial', '무료로 시작하기')}
            sx={{ ...PUBLIC_CTA_SX, ...HERO_CTA_COMPACT_SX, flex: { xs: 1, sm: 'none' }, bgcolor: CM.primary, boxShadow: 'none', '&:hover': { bgcolor: CM.primaryDark, boxShadow: 'none' } }}
          >
            무료로 시작하기
          </Button>
          <Button
            component={RouterLink}
            href={paths.home.features}
            variant="outlined"
            onClick={() => trackPublicCta('home.hero.features', '기능 둘러보기')}
            sx={{ ...PUBLIC_CTA_SX, ...HERO_CTA_COMPACT_SX, flex: { xs: 1, sm: 'none' }, color: CM.text, borderColor: CM.border, '&:hover': { borderColor: CM.primary, bgcolor: CM.alpha(0.04) } }}
          >
            기능 둘러보기
          </Button>
        </Stack>
      </Box>

      <Box sx={{ maxWidth: PUBLIC_MAX_WIDTH, mx: 'auto', mt: { xs: 5, md: 8 } }}>
        <HeroVideo />
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

/**
 * 첫 화면 아래 시연 영상. 서버 렌더와 첫 그림은 포스터(`<picture>`)이고, 마운트 뒤 폭에 맞는 영상을 그 위에 얹는다.
 * ⚠️ 움직임을 줄이라는 설정(`prefers-reduced-motion`)이면 영상을 얹지 않고 포스터만 둔다.
 * 비율 상자(`aspectRatio`)가 자리를 먼저 잡으므로 영상이 늦게 와도 아래가 밀리지 않는다.
 */
function HeroVideo() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const media = isMobile ? HERO_MEDIA.mobile : HERO_MEDIA.desktop;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: { xs: 480, sm: 'none' },
        mx: 'auto',
        aspectRatio: { xs: '1080 / 1350', sm: '1440 / 900' },
        borderRadius: { xs: 2, md: 3 },
        overflow: 'hidden',
        bgcolor: CM.bgMuted,
        border: `1px solid ${CM.border}`,
        boxShadow: '0 16px 48px rgba(17, 24, 39, 0.10)',
      }}
    >
      <Box component="picture" sx={{ position: 'absolute', inset: 0 }}>
        <source media={theme.breakpoints.down('sm').replace('@media ', '')} srcSet={HERO_MEDIA.mobile.poster} />
        <Box
          component="img"
          src={HERO_MEDIA.desktop.poster}
          alt={HERO_ALT}
          sx={{ width: 1, height: 1, objectFit: 'cover', display: 'block' }}
        />
      </Box>
      {mounted && !reduceMotion && (
        <Box
          key={isMobile ? 'mobile' : 'desktop'}
          component="video"
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          poster={media.poster}
          aria-label={HERO_ALT}
          sx={{ position: 'absolute', inset: 0, width: 1, height: 1, objectFit: 'cover', display: 'block' }}
        >
          {media.webm && <source src={media.webm} type="video/webm" />}
          <source src={media.mp4} type="video/mp4" />
        </Box>
      )}
    </Box>
  );
}
